import * as Sentry from '@sentry/react';
import { Button, Menu, MenuItemProps, Swatch, Tooltip, useToast } from '@televet/kibble-ui';
import { Box, Flex } from '@televet/kibble-ui/build/chakra';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Task } from 'pages/Tasks/types';
import React, { forwardRef, useCallback, useMemo } from 'react';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useTracking } from 'shared/providers/TrackingProvider';
import { TasksEvents } from 'shared/providers/TrackingProvider/EventNames';
import { TaskStatus, useUpdateOneTaskMutation } from 'shared/types/graphql';
import TaskStatusMenuButton from './TaskStatusMenuButton';

type TaskStatusSelectorProps = {
  task: Task;
  onStatusChange?: (status: TaskStatus) => void;
  canRevertComplete?: boolean;
  hideCompleteOption?: boolean;
  showBadge?: boolean;
  showLabel?: boolean;
};

export const TaskStatusSelector = ({
  task,
  canRevertComplete,
  onStatusChange,
  hideCompleteOption,
  showBadge,
  showLabel,
}: TaskStatusSelectorProps): JSX.Element => {
  const toast = useToast();
  const { currentClinicId, clinicUserId } = useClinicUser();
  const [updateTask] = useUpdateOneTaskMutation();
  const { trackEvent } = useTracking();

  const updateTaskStatus = useCallback(
    async (status: TaskStatus) => {
      const isCompleted = status === TaskStatus.Completed;
      try {
        await updateTask({
          variables: {
            where: {
              id: task.id,
            },
            data: {
              status,
              completedAt: isCompleted ? new Date() : null,
              completedByUserId: isCompleted ? clinicUserId : null,
            },
          },
        });

        if (isCompleted) {
          trackEvent(TasksEvents.MarkedCompleted, {
            clinicId: currentClinicId,
            fromConversations: true,
          });
        } else {
          trackEvent(TasksEvents.StatusUpdated, {
            status,
            taskId: task.id,
            clinicId: currentClinicId,
            fromConversations: true,
          });
        }
        onStatusChange?.(status);
      } catch (e) {
        Sentry.captureException(e);
        toast({
          status: 'error',
          description: 'Oops, an error occurred while updating the task.',
        });
      }
    },
    [updateTask, task.id, clinicUserId, onStatusChange, trackEvent, currentClinicId, toast],
  );

  const taskIsCompleteAndCannotRevert = !canRevertComplete && task?.status === TaskStatus.Completed;

  const menuItems = useMemo((): MenuItemProps[] => {
    const items = [
      {
        label: 'New',
        value: TaskStatus.Open,
        groupLabel: 'Needs Attention',
        leftElement: <Swatch borderColor="border.default" backgroundColor="text.success" />,
        onSelect: (): Promise<void> => updateTaskStatus(TaskStatus.Open),
      },
      {
        label: 'In Progress',
        value: TaskStatus.InProgress,
        groupLabel: 'Needs Attention',
        leftElement: <Swatch borderColor="border.default" backgroundColor="text.warning" />,
        onSelect: (): Promise<void> => updateTaskStatus(TaskStatus.InProgress),
      },
    ];
    if (!hideCompleteOption) {
      items.push({
        label: 'Completed',
        value: TaskStatus.Completed,
        groupLabel: 'Complete',
        leftElement: <Swatch borderColor="border.default" backgroundColor="text.info" />,
        onSelect: (): Promise<void> => updateTaskStatus(TaskStatus.Completed),
      });
    }

    return items;
  }, [hideCompleteOption, updateTaskStatus]);

  const MenuButton = forwardRef<HTMLDivElement>((props, _ref) => {
    return (
      <Box {...props} ref={_ref} p={0}>
        <TaskStatusMenuButton
          {...props}
          status={task?.status}
          disabled={taskIsCompleteAndCannotRevert}
          showBadge={showBadge}
        />
      </Box>
    );
  });
  MenuButton.displayName = 'MenuButton';

  return (
    <Flex direction="column" justifyContent="flex-end">
      {showLabel && (
        <Text size="sm" as="p" mb={1} textAlign="right">
          Task Status
        </Text>
      )}
      <Flex gap={1} alignItems="center" data-testid={`task-status-selector-${task.id}`}>
        {taskIsCompleteAndCannotRevert ? (
          // Small hack to disable the button without changing its appearance
          <TaskStatusMenuButton status={task?.status} disabled={taskIsCompleteAndCannotRevert} showBadge={showBadge} />
        ) : (
          <Menu
            isLazy={true}
            buttonProps={{
              as: MenuButton,
              disabled: true,
            }}
            listProps={{
              isGrouped: !hideCompleteOption,
              menuItems,
              shouldUsePortal: true,
            }}
          />
        )}
        {!hideCompleteOption && task?.status !== TaskStatus.Completed && (
          <Tooltip label="Mark as Complete" placement="top">
            <Button
              variant="secondarySubtle"
              size="xs"
              iconName="checkmark"
              borderRadius="sm"
              onClick={(): Promise<void> => updateTaskStatus(TaskStatus.Completed)}
            />
          </Tooltip>
        )}
      </Flex>
    </Flex>
  );
};
